import React, { useState, useRef, useMemo } from "react";
import { Box, Button, Container, Typography, Snackbar, Alert, CircularProgress, LinearProgress } from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx";
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

// Base URL for the backend API
const path = `http://192.168.1.248:3107`;

// Simple UUID-like ID generator for unique row IDs
const generateUniqueId = () => {
    return `row-${Math.random().toString(36).substr(2, 9)}-${Date.now().toString(36)}`;
};

const InsertClientData = () => {
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const [file, setFile] = useState(null);
    const [data, setData] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [dataInserted, setDataInserted] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [progressValue, setProgressValue] = useState(0);
    const [bufferValue, setBufferValue] = useState(0);
    const [showBuffer, setShowBuffer] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    // Update progress
    const updateProgress = (value) => {
        const clipped = Math.max(0, Math.min(100, value));
        setProgressValue(clipped);
        setBufferValue(Math.min(100, clipped + 10));
    };

    // Handle file selection
    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            const validExtensions = [".xlsx", ".xls", ".csv"];
            const fileExtension = selectedFile.name.slice(selectedFile.name.lastIndexOf(".")).toLowerCase();
            if (!validExtensions.includes(fileExtension)) {
                setSnackbar({ open: true, message: "Only Excel (.xlsx, .xls) or CSV (.csv) files are allowed", severity: "error" });
                setFile(null);
                setData([]);
                setDataInserted(false);
                fileInputRef.current.value = "";
                return;
            }
            setFile(selectedFile);
            setShowBuffer(true);
            setDataInserted(false);
            updateProgress(3);
            processFile(selectedFile);
        }
    };

    // Process the uploaded file
    const processFile = (file) => {
        const reader = new FileReader();

        reader.onprogress = (e) => {
            if (e.lengthComputable) {
                const percentRead = Math.round((e.loaded / e.total) * 100);
                const mapped = Math.round(percentRead * 0.5);
                updateProgress(mapped);
            }
        };

        reader.onload = (e) => {
            try {
                const binaryData = e.target.result;
                const workbook = XLSX.read(binaryData, { type: "binary" });
                const sheetName = workbook.SheetNames[0];
                const sheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });

                const expectedHeaders = ["Company Name", "Client Name", "MobileNo", "ProductKey"];
                const headers = jsonData[0] || [];
                const headersValid = expectedHeaders.every((header, index) => header === headers[index]);

                if (!headersValid) {
                    setSnackbar({ open: true, message: "Invalid file format. Expected headers: Company Name, Client Name, MobileNo, ProductKey", severity: "error" });
                    setData([]);
                    setDataInserted(false);
                    updateProgress(0);
                    setShowBuffer(false);
                    return;
                }

                const parsedData = jsonData.slice(1).map((row) => ({
                    id: generateUniqueId(),
                    CompanyName: row[0] || "",
                    ContactPerson: row[1] || "",
                    MobileNo: row[2] || "",
                    ProductKey: row[3] || ""
                })).filter(row => row.CompanyName || row.ContactPerson || row.MobileNo || row.ProductKey);

                setData(parsedData);

                console.group(`Parsed ${parsedData.length} records from ${file.name}`);
                parsedData.forEach((r, i) => {
                    console.log(`Row ${i}:`, {
                        id: r.id,
                        CompanyName: r.CompanyName,
                        ContactPerson: r.ContactPerson,
                        MobileNo: r.MobileNo,
                        ProductKey: r.ProductKey
                    });
                });
                console.groupEnd();

                updateProgress(50);
                setSnackbar({ open: true, message: `Successfully parsed ${parsedData.length} records`, severity: "success" });

                updateProgress(100);
                setTimeout(() => {
                    setShowBuffer(false);
                    updateProgress(0);
                }, 800);
            } catch (err) {
                console.error(err);
                setSnackbar({ open: true, message: "Error parsing file", severity: "error" });
                setData([]);
                setDataInserted(false);
                updateProgress(0);
                setShowBuffer(false);
            }
        };

        reader.onerror = () => {
            setSnackbar({ open: true, message: "Error reading file", severity: "error" });
            setData([]);
            setDataInserted(false);
            updateProgress(0);
            setShowBuffer(false);
        };

        reader.readAsBinaryString(file);
    };

    // Handle upload to backend
    const handleUploadSubmit = async () => {
        if (!data.length) {
            setSnackbar({ open: true, message: "No valid data to upload", severity: "error" });
            return;
        }

        setUploading(true);
        setUploadProgress(0);
        updateProgress(50);
        setShowBuffer(true);

        try {
            const payload = data.map(row => ({
                CompanyName: row.CompanyName,
                ContactPerson: row.ContactPerson,
                MobileNo: row.MobileNo,
                ProductKey: row.ProductKey
            }));

            console.log("Uploading payload:", payload);

            // const response = await axios.post(`${path}/api/members/bulk-insert`, payload, {
            //     onUploadProgress: (progressEvent) => {
            //         const percentCompleted = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
            //         setUploadProgress(percentCompleted);
            //         const overall = 50 + Math.round(percentCompleted * 0.5);
            //         updateProgress(overall);
            //     }
            // });

            // if (response.status === 201) {
            //     setSnackbar({ open: true, message: `Successfully uploaded ${data.length} records`, severity: "success" });
            //     setDataInserted(true);
            //     setFile(null);
            //     fileInputRef.current.value = "";
            //     updateProgress(100);

            //     if (Array.isArray(response.data.errors) && response.data.errors.length) {
            //         console.warn("Server reported errors:", response.data.errors);
            //     }

            //     setTimeout(() => {
            //         setShowBuffer(false);
            //         updateProgress(0);
            //     }, 800);
            // } else {
            //     setSnackbar({ open: true, message: response.data.message || "Failed to upload data", severity: "error" });
            // }
        } catch (error) {
            console.error("Upload error:", error.response?.data || error.message);
            setSnackbar({ open: true, message: "Failed to upload data to server", severity: "error" });
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    // Handle back click
    const handleBackClick = () => { navigate("/main/key-options"); };

    // DataGrid columns
    const columns = useMemo(() => [
        { field: "CompanyName", headerName: "Company Name", flex: 2, minWidth: 180 },
        { field: "ContactPerson", headerName: "Client Name", flex: 1.6, minWidth: 150 },
        { field: "MobileNo", headerName: "Mobile No", flex: 1, minWidth: 140 },
        { field: "ProductKey", headerName: "Product Key", flex: 1.4, minWidth: 160 }
    ], []);

    // Generate a unique key for DataGrid to force re-render when data changes
    const dataGridKey = useMemo(() => data.map(row => row.id).join('-'), [data]);

    return (
        <Container sx={{ pt: 3, maxWidth: "1300px !important" }}>
            <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: "#1976D2" }}>Insert Client Data</Typography>
                <Button variant="contained" onClick={handleBackClick} sx={{ height: 40, bgcolor: "#1877F2", "&:hover": { bgcolor: "#145db5" }, borderRadius: 2, textTransform: "none", fontWeight: "medium", px: 2 }}>
                    Back to Key Options
                </Button>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2, alignItems: { xs: "stretch", sm: "center" } }}>
                    <input type="file" accept=".xlsx,.xls,.csv" onChange={handleFileChange} ref={fileInputRef} style={{ display: "none" }} id="file-upload" />
                    <Button variant="contained" onClick={() => fileInputRef.current.click()} sx={{ height: 40, bgcolor: "#1877F2", "&:hover": { bgcolor: "#145db5" }, borderRadius: 2, textTransform: "none", fontWeight: "medium", px: 2 }}>
                        Choose File
                    </Button>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body2" sx={{ color: "#555" }}>
                            {file ? file.name : "No file selected (Excel or CSV only)"}
                        </Typography>
                    </Box>

                    {(file || (dataInserted && data.length > 0)) && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Button
                                variant="contained"
                                onClick={handleUploadSubmit}
                                disabled={uploading || !data.length}
                                sx={{
                                    height: 40,
                                    bgcolor: "#1976D2",
                                    "&:hover": { bgcolor: "#115293" },
                                    borderRadius: 2,
                                    textTransform: "none",
                                    fontWeight: "medium",
                                    px: 2,
                                    "&.Mui-disabled": { bgcolor: "#bdbdbd", color: "#666" }
                                }}
                            >
                                {uploading ? <CircularProgress size={24} color="inherit" /> : <><InsertDriveFileIcon sx={{ mr: 1 }} /> Insert</>}
                            </Button>
                            <Box sx={{ width: 240 }}>
                                {showBuffer ? (
                                    <>
                                        <LinearProgress variant="buffer" value={progressValue} valueBuffer={bufferValue} sx={{ height: 8, borderRadius: 4 }} />
                                        <Typography variant="caption" sx={{ mt: 0.5, textAlign: "center" }}>
                                            {uploading ? `Uploading: ${uploadProgress}%` : `${progressValue}%`}
                                        </Typography>
                                    </>
                                ) : (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}></Box>
                                )}
                            </Box>
                        </Box>
                    )}
                </Box>

                {data.length > 0 && (
                    <Box sx={{ maxHeight: "80vh" }}>
                        <DataGrid
                            key={dataGridKey}
                            rows={data}
                            columns={columns}
                            pageSize={70}
                            rowsPerPageOptions={[70, 150, 250]}
                            components={{ Toolbar: GridToolbar }}
                            autoHeight
                            componentsProps={{ toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 500 } } }}
                            sx={{
                                borderRadius: 2,
                                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                '& .MuiDataGrid-columnHeaders': { backgroundColor: '#165bb5', color: '#FFFFFF', fontWeight: 'bold' },
                                '& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-columnHeaderTitleContainer': { color: '#FFFFFF' },
                                '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': { color: '#FFFFFF !important', opacity: 1 },
                                '& .MuiDataGrid-columnHeader .MuiIconButton-root': { color: '#FFFFFF !important', opacity: 1 },
                                '& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon, & .MuiDataGrid-columnHeader .MuiDataGrid-filterIcon, & .MuiDataGrid-columnHeader .MuiDataGrid-sortIcon': { color: '#FFFFFF !important', opacity: 1 },
                                '& .MuiDataGrid-columnHeader .MuiDataGrid-sortIcon svg, & .MuiDataGrid-columnHeader .MuiDataGrid-sortIcon svg path': { fill: '#FFFFFF !important', stroke: '#FFFFFF !important', color: '#FFFFFF !important', opacity: 1 },
                                '& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon svg path, & .MuiDataGrid-columnHeader .MuiDataGrid-filterIcon svg path': { fill: '#FFFFFF !important', stroke: '#FFFFFF !important', opacity: 1 },
                                '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: '#FFFFFF' },
                                '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: '#ecf4fc' },
                                '& .highlight-row': { animation: 'colorTransition 2s infinite alternate' },
                                '@keyframes colorTransition': { '0%': { backgroundColor: '#FDB68B' }, '100%': { backgroundColor: '#F4A261' } },
                                '& .mobile-missing': { animation: 'mobilePulse 3s ease-in-out infinite' },
                                '@keyframes mobilePulse': { '0%': { backgroundColor: '#fff3e0' }, '100%': { backgroundColor: '#ffb74d' } },
                                '& .mobile-zero': { animation: 'mobileZeroPulse 2.8s ease-in-out infinite' },
                                '@keyframes mobileZeroPulse': { '0%': { backgroundColor: '#fff8e1' }, '50%': { backgroundColor: '#ffcc80' }, '100%': { backgroundColor: '#ffb300' } },
                                '& .client-missing': { animation: 'clientPulse 2.6s ease-in-out infinite' },
                                '@keyframes clientPulse': { '0%': { backgroundColor: '#fff3f3' }, '50%': { backgroundColor: '#ffd6d6' }, '100%': { backgroundColor: '#ffb3b3' } },
                                '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': { outline: 'none' }
                            }}
                            getRowClassName={(params) => {
                                const name = params.row.ContactPerson;
                                const mobile = params.row.MobileNo;

                                if (!name || (typeof name === 'string' && name.trim() === '') || (typeof name === 'string' && /^no\s*name$/i.test(name.trim()))) {
                                    return 'client-missing';
                                }
                                if (mobile === undefined || mobile === null || (String(mobile).trim() === '')) {
                                    return 'mobile-missing';
                                }
                                if (String(mobile).trim() === '0') {
                                    return 'mobile-zero';
                                }
                                return '';
                            }}
                        />
                    </Box>
                )}
            </Box>

            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default InsertClientData;